import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useCampusAuth } from "@/contexts/CampusAuth";
import { Gauge, Shield } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MODULE_KEYS = ["sis", "massnahmen", "pflegebericht", "kassenbrief", "hoehergruppe", "qualitaet", "nba"];
const MODULE_LABELS = {
  sis: "SIS", massnahmen: "Maßn.", pflegebericht: "Bericht",
  kassenbrief: "Kasse", hoehergruppe: "Höherst.", qualitaet: "Q-Check", nba: "NBA",
};

export default function CampusPflegeKiAdmin() {
  const { user } = useCampusAuth();
  const [today, setToday] = useState(null);
  const [limits, setLimits] = useState(null);
  const [overrideEmail, setOverrideEmail] = useState("");
  const [overrideLimit, setOverrideLimit] = useState(50);
  const [busy, setBusy] = useState(false);
  const [freigaben, setFreigaben] = useState(null);

  const load = useCallback(async () => {
    try {
      const [t, l, f] = await Promise.all([
        axios.get(`${API}/pflege-ki/ratelimit/admin/today`, { withCredentials: true }),
        axios.get(`${API}/pflege-ki/ratelimit/admin/limits`, { withCredentials: true }),
        axios.get(`${API}/pflege-ki/freigabe/admin?limit=50`, { withCredentials: true }),
      ]);
      setToday(t.data);
      setLimits(l.data);
      setFreigaben(f.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Konnte Admin-Daten nicht laden.");
    }
  }, []);

  useEffect(() => { if (user?.is_admin) load(); }, [user, load]);

  if (!user?.is_admin) {
    return (
      <div data-testid="pki-admin-locked" className="rounded-3xl border border-cream-300 bg-white p-8">
        <div className="text-[10.5px] uppercase tracking-[0.28em] font-bold text-brand-darkred mb-2">Admin only</div>
        <p className="text-sand-700">Diese Übersicht ist Admins vorbehalten.</p>
      </div>
    );
  }

  const setLimit = async () => {
    if (!overrideEmail.includes("@") || !overrideLimit) return;
    setBusy(true);
    try {
      await axios.post(`${API}/pflege-ki/ratelimit/admin/limits`,
        { user_email: overrideEmail.trim(), daily_limit: Number(overrideLimit) },
        { withCredentials: true });
      toast.success(`Limit für ${overrideEmail} auf ${overrideLimit}/Tag/Modul gesetzt.`);
      setOverrideEmail("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Konnte Limit nicht setzen.");
    } finally { setBusy(false); }
  };

  const deleteLimit = async (email) => {
    setBusy(true);
    try {
      await axios.delete(`${API}/pflege-ki/ratelimit/admin/limits/${encodeURIComponent(email)}`,
        { withCredentials: true });
      toast.success(`Override für ${email} entfernt.`);
      await load();
    } catch (e) {
      toast.error("Konnte Override nicht entfernen.");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="pki-admin-page">
      <div>
        <div className="text-[10.5px] uppercase tracking-[0.32em] font-semibold text-clay-dark">
          Admin · Pflege-KI Schutzfunktionen
        </div>
        <h1 className="font-display text-3xl md:text-4xl text-sand-800 mt-1">
          Rate-Limits & Freigabe-Audit
        </h1>
        <p className="text-sand-600 text-[14.5px] mt-2 max-w-3xl">
          Steuere Tageslimits pro User · sieh aktuelle Nutzung pro Modul · prüfe wer
          welche KI-Entwürfe freigegeben und versendet hat.
        </p>
      </div>

      {/* Heutige Nutzung */}
      <div className="rounded-3xl border border-cream-300 bg-white p-6"
        data-testid="pki-admin-usage-section">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Gauge className="h-5 w-5 text-clay-dark" strokeWidth={2} />
            <div>
              <div className="text-[10.5px] uppercase tracking-[0.28em] font-bold text-clay-dark">
                Heutige Nutzung · {today?.date}
              </div>
              <div className="text-[12px] text-sand-500">
                Default-Limit: {today?.default_limit || 50} / Tag / Modul
              </div>
            </div>
          </div>
          <Button onClick={load} disabled={busy} variant="outline" size="sm"
            data-testid="pki-admin-reload"
            className="h-9 rounded-full border-clay">Neu laden</Button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="text-left text-sand-500 text-[11px] uppercase tracking-[0.18em]">
                <th className="py-2 pr-3">User</th>
                <th className="py-2 pr-3">Limit</th>
                <th className="py-2 pr-3">Total heute</th>
                {MODULE_KEYS.map((k) => (
                  <th key={k} className="py-2 pr-2 text-center">{MODULE_LABELS[k]}</th>
                ))}
                <th className="py-2 pr-3">Letzte Nutzung</th>
                <th className="py-2 pr-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {today?.users?.length ? today.users.map((u) => (
                <tr key={u.user_email} className="border-t border-cream-200"
                  data-testid={`pki-admin-row-${u.user_email}`}>
                  <td className="py-2 pr-3 font-mono text-[12px]">{u.user_email}</td>
                  <td className="py-2 pr-3">{u.daily_limit}</td>
                  <td className="py-2 pr-3 font-semibold">{u.total_today}</td>
                  {MODULE_KEYS.map((k) => {
                    const v = u.by_module[k] || 0;
                    const reached = v >= u.daily_limit;
                    return (
                      <td key={k} className={`py-2 px-2 text-center font-mono ${
                        reached ? "bg-brand-darkred/15 text-brand-darkred font-bold rounded" : "text-sand-700"
                      }`}>{v}</td>
                    );
                  })}
                  <td className="py-2 pr-3 text-[11.5px] text-sand-500">
                    {u.last_module} · {u.last_used_at ? new Date(u.last_used_at).toLocaleTimeString("de-DE") : "—"}
                  </td>
                  <td className="py-2 pr-3">
                    {u.limit_reached ? (
                      <span className="inline-flex items-center px-2 h-6 rounded-full bg-brand-darkred text-cream-50 text-[10px] font-bold uppercase tracking-[0.15em]">
                        Limit erreicht
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 h-6 rounded-full bg-sage-dark/15 text-sage-dark text-[10px] font-bold uppercase tracking-[0.15em]">
                        OK
                      </span>
                    )}
                  </td>
                </tr>
              )) : (
                <tr><td colSpan={MODULE_KEYS.length + 5} className="py-6 text-center text-sand-500">
                  Heute noch keine KI-Aktivität.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Limit-Overrides */}
      <div className="rounded-3xl border border-cream-300 bg-cream-50 p-6"
        data-testid="pki-admin-limits-section">
        <div className="text-[10.5px] uppercase tracking-[0.28em] font-bold text-clay-dark mb-3">
          User-spezifische Limit-Overrides
        </div>
        <div className="flex flex-wrap items-end gap-2 mb-4">
          <div>
            <label className="block text-[10px] uppercase tracking-[0.22em] font-semibold text-sand-500 mb-1">User-E-Mail</label>
            <input value={overrideEmail} onChange={(e) => setOverrideEmail(e.target.value)}
              data-testid="pki-admin-override-email"
              placeholder="user@example.de"
              className="h-10 w-72 max-w-full rounded-xl border border-cream-300 bg-white px-3 text-[13px]" />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.22em] font-semibold text-sand-500 mb-1">Tageslimit / Modul</label>
            <input type="number" min={1} max={10000}
              value={overrideLimit} onChange={(e) => setOverrideLimit(e.target.value)}
              data-testid="pki-admin-override-limit"
              className="h-10 w-32 rounded-xl border border-cream-300 bg-white px-3 text-[13px]" />
          </div>
          <Button onClick={setLimit} disabled={busy || !overrideEmail.includes("@")}
            data-testid="pki-admin-override-save"
            className="h-10 rounded-full bg-sand-800 hover:bg-sand-700 text-cream-50 px-5">
            Limit setzen
          </Button>
        </div>
        <div className="space-y-1.5">
          {limits?.overrides?.length ? limits.overrides.map((o) => (
            <div key={o.user_email}
              data-testid={`pki-admin-override-row-${o.user_email}`}
              className="flex items-center justify-between gap-2 rounded-xl border border-cream-300 bg-white px-3 py-2">
              <div className="text-[12.5px]">
                <span className="font-mono text-sand-800">{o.user_email}</span>
                <span className="mx-2 text-sand-500">→</span>
                <span className="font-semibold text-terra">{o.daily_limit}/Tag/Modul</span>
                <span className="ml-2 text-[11px] text-sand-500">durch {o.set_by} · {o.set_at?.slice(0, 10)}</span>
              </div>
              <button onClick={() => deleteLimit(o.user_email)}
                data-testid={`pki-admin-override-delete-${o.user_email}`}
                className="text-[11.5px] text-brand-darkred hover:underline">entfernen</button>
            </div>
          )) : (
            <div className="text-[12.5px] text-sand-500 italic">
              Keine User-Overrides aktiv · alle nutzen Default-Limit {today?.default_limit || 50}/Tag/Modul.
            </div>
          )}
        </div>
      </div>

      {/* Freigabe-Audit-Trail */}
      <div className="rounded-3xl border border-cream-300 bg-white p-6"
        data-testid="pki-admin-freigaben-section">
        <div className="flex items-center gap-2 mb-3">
          <Shield className="h-5 w-5 text-brand-darkred" strokeWidth={2} />
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.28em] font-bold text-brand-darkred">
              Audit-Trail · Pflegefachliche Freigaben
            </div>
            <div className="text-[12px] text-sand-500">Letzte {freigaben?.count || 0} Freigaben</div>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="text-left text-sand-500 text-[11px] uppercase tracking-[0.18em]">
                <th className="py-2 pr-3">Datum</th>
                <th className="py-2 pr-3">Dokument</th>
                <th className="py-2 pr-3">Kunde</th>
                <th className="py-2 pr-3">Prüfer:in</th>
                <th className="py-2 pr-3">Rolle</th>
                <th className="py-2 pr-3">User</th>
                <th className="py-2 pr-3">Bearb.?</th>
                <th className="py-2 pr-3">Mails</th>
              </tr>
            </thead>
            <tbody>
              {freigaben?.freigaben?.length ? freigaben.freigaben.map((f) => (
                <tr key={f.id} className="border-t border-cream-200"
                  data-testid={`pki-admin-freigabe-row-${f.id.slice(0, 8)}`}>
                  <td className="py-2 pr-3 text-[11.5px] font-mono">
                    {f.freigegeben_at ? new Date(f.freigegeben_at).toLocaleString("de-DE") : "—"}
                  </td>
                  <td className="py-2 pr-3 font-semibold">{f.draft_kind}</td>
                  <td className="py-2 pr-3">{f.customer_label || "—"}</td>
                  <td className="py-2 pr-3">{f.pruefer_name}</td>
                  <td className="py-2 pr-3 text-sand-600">{f.pruefer_rolle}</td>
                  <td className="py-2 pr-3 text-[11.5px] font-mono text-sand-500">{f.user_email}</td>
                  <td className="py-2 pr-3">{f.edited_before ? "ja" : "nein"}</td>
                  <td className="py-2 pr-3">{(f.mail_log || []).length}</td>
                </tr>
              )) : (
                <tr><td colSpan={8} className="py-6 text-center text-sand-500">
                  Noch keine Freigaben im System.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
